import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { X, Check, AlertTriangle, Clock, Pencil } from "lucide-react";

// ✅ ADDED: Constant for 4 hours in seconds
const FOUR_HOURS_IN_SECONDS = 4 * 60 * 60; // 14400

// 1. Generate a list of Financial Years (Past 2, Current, Future 2)
const generateFYList = () => {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = -2; i <= 2; i++) {
    const fyStartYear = currentYear + i;
    const fyEndYear = fyStartYear + 1;
    years.push({
      label: `FY ${fyStartYear}-${String(fyEndYear).slice(-2)}`,
      start_date: `${fyStartYear}-04-01`,
      end_date: `${fyEndYear}-03-31`
    });
  }
  return years;
};

// 2. Find which rate applies to today (for normal display)
const getCurrentFYRate = (rates) => {
  if (!rates || rates.length === 0) return null;
  const today = new Date().toISOString().split('T')[0];
  let current = rates.find(r => today >= r.start_date && today <= r.end_date);
  if (!current) current = rates[rates.length - 1];
  return current;
};

// 3. Find what the default dropdown value should be
const getDefaultFYLabel = () => {
  const today = new Date();
  const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return `FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`;
};

// Helper to get local YYYY-MM-DD safely
const getTodayLocal = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


// ===== ADDED: Inline Name Editor Component =====
function InlineNameEditor({ user, onSave, onCancel }) {
  const [name, setName] = useState(user.name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Name cannot be empty");
    if (trimmed === user.name) return onCancel();
    
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}/name`, { name: trimmed });
      toast.success(`Name updated to "${trimmed}"`);
      onSave(trimmed);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") onCancel();
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        className="border border-[var(--ess-ink)] px-2 py-1 text-sm font-medium bg-white outline-none"
        disabled={saving}
        data-testid={`name-input-${user.id}`}
      />
      <button
        onClick={handleSave}
        disabled={saving || !name.trim()}
        className="text-green-600 hover:text-green-800 disabled:opacity-50"
        data-testid={`name-save-${user.id}`}
      >
        <Check size={16} />
      </button>
      <button
        onClick={onCancel}
        disabled={saving}
        className="text-gray-400 hover:text-gray-600"
        data-testid={`name-cancel-${user.id}`}
      >
        <X size={16} />
      </button>
    </div>
  );
}


function BillableRateCell({ u, onChange }) {
  const fyList = generateFYList();
  const activeRate = getCurrentFYRate(u.billable_rates);
  
  const [selectedFY, setSelectedFY] = useState(fyList.find(f => f.label === getDefaultFYLabel()));
  const [val, setVal] = useState(activeRate?.rate ?? "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      setVal(activeRate?.rate ?? "");
    }
  }, [activeRate, editing]);

  const handleFYChange = (e) => {
    const newFY = fyList.find(f => f.label === e.target.value);
    setSelectedFY(newFY);
    const existingRateForFY = u.billable_rates?.find(r => r.label === newFY.label);
    setVal(existingRateForFY?.rate ?? "");
  };

  const save = async () => {
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) return toast.error("Enter a valid non-negative number");
    setSaving(true);
    try {
      await api.patch("/users/billable-rate", {
        user_id: u.id,
        label: selectedFY.label,
        start_date: selectedFY.start_date,
        end_date: selectedFY.end_date,
        rate: num
      });
      toast.success(`Rate updated for ${selectedFY.label}`);
      setEditing(false);
      onChange(); 
    } catch (e) { 
      toast.error(formatApiError(e)); 
    }
    finally { 
      setSaving(false); 
    }
  };

  return (
    <div className="flex items-center gap-2">
      {editing ? (
        <>
          <select value={selectedFY.label} onChange={handleFYChange} className="border border-gray-200 px-1 py-1 text-xs rounded bg-white" disabled={saving}>
            {fyList.map(fy => (
              <option key={fy.label} value={fy.label}>{fy.label}</option>
            ))}
          </select>
          <input type="number" min="0" step="any" value={val} onChange={(e) => setVal(e.target.value)} className="border border-gray-200 px-2 py-1 text-sm w-20 tabular-nums" data-testid={`rate-input-${u.id}`} autoFocus />
          <button onClick={save} disabled={saving} className="text-xs uppercase tracking-wider underline text-gray-600 hover:text-[var(--ess-ink)] disabled:text-gray-400" data-testid={`rate-save-${u.id}`}>
            {saving ? "..." : "Save"}
          </button>
        </>
      ) : (
        <>
          <span className="text-sm tabular-nums text-gray-700">
            {activeRate ? `${activeRate.label}: ₹ ${activeRate.rate}` : <span className="text-gray-400 italic">No rate set</span>}
          </span>
          <button onClick={() => setEditing(true)} className="text-xs uppercase tracking-wider underline text-gray-600 hover:text-[var(--ess-ink)]" data-testid={`rate-change-${u.id}`}>
            Change
          </button>
        </>
      )}
    </div>
  );
}


function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.post("/users/reset-password", { user_id: user.id, new_password: pw });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="reset-pw-modal">
      <div className="bg-white border border-gray-200 w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Reset password</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Set a new password for <span className="font-medium text-[var(--ess-ink)]">{user.name}</span> ({user.email}). Please share it with them securely.</p>
        <form onSubmit={submit}>
          <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6 chars)" className="w-full border border-gray-200 px-3 py-2 text-sm mb-4" data-testid="new-password-input" required minLength={6} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="border border-gray-200 px-4 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider" data-testid="reset-pw-save">{saving ? "Saving..." : "Reset"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [resetUser, setResetUser] = useState(null);
  const isAdmin = user?.role === "admin";
  
  // ADDED: Track which user's name is being edited
  const [editingNameId, setEditingNameId] = useState(null);
  
  const [selectedDate, setSelectedDate] = useState(getTodayLocal);
  const [showMissing, setShowMissing] = useState(false);
  const [showLowHours, setShowLowHours] = useState(false);

  const load = async () => {
    try {
      const [uRes, eRes] = await Promise.all([
        api.get("/users"), 
        api.get("/time-entries", { params: { start_date: selectedDate, end_date: selectedDate } })
      ]);
      setUsers(uRes.data);
      setEntries(eRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  
  useEffect(() => { load(); }, [selectedDate]);

  const userSecondsOnDate = new Map();
  entries.forEach(e => {
    if (e.date === selectedDate) {
      userSecondsOnDate.set(e.user_id, (userSecondsOnDate.get(e.user_id) || 0) + (e.duration_seconds || 0));
    }
  });

  const isMissingOnDate = (u) => {
    if (u.active === false || u.id === user.id) return false;
    const totalSeconds = userSecondsOnDate.get(u.id) || 0;
    return totalSeconds === 0;
  };

  const isLowHoursOnDate = (u) => {
    if (u.active === false || u.id === user.id) return false;
    const totalSeconds = userSecondsOnDate.get(u.id) || 0;
    return totalSeconds > 0 && totalSeconds < FOUR_HOURS_IN_SECONDS;
  };

  // ADDED: Handle name update
  const handleNameUpdate = (userId, newName) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, name: newName } : u));
    setEditingNameId(null);
  };

  const setRole = async (u, role) => {
    try {
      await api.post("/users/role", { user_id: u.id, role });
      toast.success("Role updated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email}) and ALL their time entries? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Member deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    if (userId === user.id) return toast.error("You cannot deactivate your own account");
    const action = currentStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await api.patch(`/users/${userId}/status`, { active: !currentStatus });
      toast.success(`User ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = users.filter((u) => {
    const anyFilterActive = showMissing || showLowHours;
    
    if (anyFilterActive) {
      const matchesMissing = showMissing && isMissingOnDate(u);
      const matchesLowHours = showLowHours && isLowHoursOnDate(u);
      if (!matchesMissing && !matchesLowHours) return false;
    }
    
    if (roleFilter && u.role !== roleFilter) return false;
    if (!search) return true;
    return u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
  });

  const missingCount = users.filter(u => isMissingOnDate(u)).length;
  const lowHoursCount = users.filter(u => isLowHoursOnDate(u)).length;
  
  const formatSelectedDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dateObj = new Date(y, m - 1, d);
    const todayStr = getTodayLocal();
    if (dateStr === todayStr) return "Today";
    return dateObj.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div data-testid="team-page">
      <div className="min-h-20 border-b border-gray-200 bg-white sticky top-0 z-20 flex items-center pl-16 lg:pl-8 pr-4 sm:pr-8 py-3">
        <div>
          <div className="ess-section-label">Manage</div>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]">Team</div>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6">
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          
          {/* Date, Missing, and <4h on the far left */}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-gray-200 px-2 py-2 text-sm"
                data-testid="missing-date-picker"
              />
              <button 
                onClick={() => setShowMissing(!showMissing)}
                className={`px-3 py-2 text-sm border flex items-center gap-2 transition-colors ${
                  showMissing 
                    ? "bg-red-50 border-red-300 text-red-700 font-medium" 
                    : "border-gray-200 text-gray-600 hover:border-red-200"
                }`}
                data-testid="missing-toggle-btn"
              >
                <AlertTriangle size={14} />
                Missing {missingCount > 0 && `(${missingCount})`}
              </button>

              <button 
                onClick={() => setShowLowHours(!showLowHours)}
                className={`px-3 py-2 text-sm border flex items-center gap-2 transition-colors ${
                  showLowHours 
                    ? "bg-amber-50 border-amber-300 text-amber-700 font-medium" 
                    : "border-gray-200 text-gray-600 hover:border-amber-200"
                }`}
                data-testid="low-hours-toggle-btn"
              >
                <Clock size={14} />
                &lt; 4h {lowHoursCount > 0 && `(${lowHoursCount})`}
              </button>
            </div>
          )}

          {/* Search by name/email and Role filter */}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className="border border-gray-200 px-4 py-2 text-sm w-80"
            data-testid="team-search"
          />

          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="role-filter">
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </select>

          <span className="text-xs text-gray-500 ml-auto">{filtered.length} / {users.length}</span>
        </div>

        <div className="border border-gray-200 overflow-x-auto">
          <table className="ess-table w-full min-w-[800px]">
            <thead>
              <tr>
                <th>Name</th>
                <th className="px-3 py-2">Email</th>
                {isAdmin && <th>Billable Rate (INR)</th>}
                <th>Role</th>
                <th>Group</th>
                {isAdmin && <th className="text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr 
  key={u.id} 
  className={`group border-b border-gray-100 ${
    !u.active ? "opacity-50 bg-gray-50" : 
    isMissingOnDate(u) ? "bg-red-50" : 
    isLowHoursOnDate(u) ? "bg-amber-50" : ""
  }`} 
  data-testid={`user-row-${u.id}`}
>
                  <td className="font-medium text-[var(--ess-ink)]">
                    <div className="flex items-center gap-2">
                      {/* ADDED: Inline name editor or display name with edit button */}
                      {isAdmin && editingNameId === u.id ? (
                        <InlineNameEditor
                          user={u}
                          onSave={(newName) => handleNameUpdate(u.id, newName)}
                          onCancel={() => setEditingNameId(null)}
                        />
                      ) : (
                        <>
                          <span>{u.name}</span>
                          {isAdmin && (
                            <button 
                              onClick={() => setEditingNameId(u.id)}
                              className="text-gray-400 hover:text-[var(--ess-ink)] opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Edit name"
                              data-testid={`edit-name-${u.id}`}
                            >
                              <Pencil size={12} />
                            </button>
                          )}
                        </>
                      )}
                      
                      {isMissingOnDate(u) && (
                        <span className="text-[10px] uppercase tracking-wider bg-red-100 text-red-700 px-1.5 py-0.5 font-bold flex items-center gap-1">
                          <AlertTriangle size={10} /> Missing
                        </span>
                      )}
                      {isLowHoursOnDate(u) && (
                        <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 font-bold flex items-center gap-1">
                          <Clock size={10} /> &lt; 4h
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">{u.email}</td>
                  {isAdmin && (
                    <td>
                      <BillableRateCell u={u} onChange={load} />
                    </td>
                  )}
                  <td>
                    <span className={`text-xs uppercase tracking-wider px-2 py-0.5 ${u.role === "admin" ? "bg-[var(--ess-ink)] text-white" : "bg-gray-100"}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>{u.group || "—"}</td>
                  
                  {isAdmin && (
                    <td className="text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-4">
                      
                        {u.id !== user.id && (
                          <>
                            {u.role !== "admin" ? (
                              <button onClick={() => setRole(u, "admin")} className="text-xs uppercase tracking-wider underline" data-testid={`promote-${u.id}`}>Make admin</button>
                            ) : (
                              <button onClick={() => setRole(u, "member")} className="text-xs uppercase tracking-wider underline text-gray-500" data-testid={`demote-${u.id}`}>Demote</button>
                            )}
                            
                            <button 
                              onClick={() => handleToggleActive(u.id, u.active)} 
                              className={`text-xs uppercase tracking-wider underline ${u.active ? "text-red-600 hover:text-red-800" : "text-green-600 hover:text-green-800"}`}
                            >
                              {u.active ? "Deactivate" : "Activate"}
                            </button>

                            <button onClick={() => deleteUser(u)} className="text-xs uppercase tracking-wider underline text-red-600" data-testid={`delete-user-${u.id}`}>Delete</button>
                          </>
                        )}
                        
                        <button onClick={() => setResetUser(u)} className="text-xs uppercase tracking-wider underline" data-testid={`reset-pw-${u.id}`}>Reset PW</button>
                        
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 4} className="text-center py-10 text-gray-500">
                  {(showMissing || showLowHours) ? "Everyone meets the requirements for the selected filters." : "No team members."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {resetUser && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
}
